import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { BrandMark } from "../components/BrandMark";
import { toast } from "sonner";
import { KeyRound, Lock, User, ArrowRight, ShieldCheck, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function CustomerLogin() {
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    // Deep-link support: notification tap routes here with ?deep=/portal?...
    // We MUST require fresh credentials before opening the portal — never
    // silently reuse an existing session or token.
    const deep = sp.get("deep") || "";

    /**
     * Merge the deep-link's existing query string with the fresh portal
     * token that was just issued by the sign-in call. The returned URL is
     * safe to navigate to and always requires a valid `t` (JWT).
     */
    const buildPortalUrl = (freshToken) => {
        if (!deep || !deep.startsWith("/portal")) {
            return `/portal?t=${encodeURIComponent(freshToken)}`;
        }
        const q = deep.split("?")[1] || "";
        const params = new URLSearchParams(q);
        params.set("t", freshToken); // always replace any stale token
        return `/portal?${params.toString()}`;
    };

    const [customerCode, setCustomerCode] = useState("");
    const [method, setMethod] = useState("password"); // "password" | "pin"
    const [credential, setCredential] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    /* mandatory change on first login */
    const [mustChange, setMustChange] = useState(false);
    const [token, setToken] = useState("");
    const [newCred, setNewCred] = useState("");
    const [newCred2, setNewCred2] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        document.title = "Customer Login | Lokdhara Microfinance";
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/customer-auth/login`, {
                customer_code: customerCode.trim(),
                login_method: method,
                credential,
            });
            if (data.must_change) {
                setToken(data.token);
                setMustChange(true);
                toast.info("Please set a new " + (method === "pin" ? "PIN" : "password") + " to continue.");
            } else {
                toast.success("Signed in.");
                navigate(buildPortalUrl(data.token));
            }
        } catch (e2) {
            setErr(formatErr(e2.response?.data?.detail) || e2.message);
        } finally {
            setLoading(false);
        }
    };

    const saveNew = async (e) => {
        e.preventDefault();
        setErr("");
        if (newCred !== newCred2) {
            setErr(method === "pin" ? "PINs do not match" : "Passwords do not match");
            return;
        }
        setSaving(true);
        try {
            const payload = { t: token };
            if (method === "pin") payload.new_pin = newCred;
            else payload.new_password = newCred;
            await axios.post(`${API}/portal/change-credential`, payload);
            toast.success("Credential updated. Signing you in…");
            navigate(buildPortalUrl(token));
        } catch (e2) {
            setErr(formatErr(e2.response?.data?.detail) || e2.message);
        } finally {
            setSaving(false);
        }
    };

    const credLabel = method === "pin" ? "Security PIN" : "Password";
    const credType = method === "pin" ? "password" : "password";
    const credPlaceholder = method === "pin" ? "4–6 digit PIN" : "Enter your password";

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-brand-soft/40 to-brand-tint/60 flex flex-col">
            <header className="max-w-7xl w-full mx-auto px-4 sm:px-6 h-20 flex items-center">
                <Link to="/home" className="flex items-center" data-testid="cl-brand">
                    <BrandMark size={48} className="sm:!h-12" />
                </Link>
            </header>

            <main className="flex-1 grid place-items-center px-4 sm:px-6 py-10">
                <Card className="w-full max-w-md rounded-3xl border-slate-100 shadow-card p-6 sm:p-8"
                    data-testid="cl-card"
                >
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center">
                            <ShieldCheck className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <div>
                            <h1 className="font-display text-xl sm:text-2xl font-bold text-brand-navy">
                                {mustChange ? "Set a new " + (method === "pin" ? "PIN" : "password") : "Customer Login"}
                            </h1>
                            <p className="text-xs text-slate-500">
                                {mustChange
                                    ? "This is required after a temporary credential."
                                    : "Sign in to your Lokdhara account."}
                            </p>
                        </div>
                    </div>

                    {!mustChange ? (
                        <form className="mt-6 space-y-4" onSubmit={submit} data-testid="cl-form">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Customer ID
                                </label>
                                <div className="relative mt-2">
                                    <User className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        value={customerCode}
                                        onChange={(e) => setCustomerCode(e.target.value.toUpperCase())}
                                        placeholder="e.g. LMF0001"
                                        className="pl-9 h-11 rounded-xl"
                                        required
                                        data-testid="cl-customer-code"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Sign in with
                                </label>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => { setMethod("password"); setCredential(""); }}
                                        className={`h-11 rounded-xl border text-sm font-semibold transition ${
                                            method === "password"
                                                ? "bg-brand-navy text-white border-brand-navy"
                                                : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                        }`}
                                        data-testid="cl-method-password"
                                    >
                                        Password
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setMethod("pin"); setCredential(""); }}
                                        className={`h-11 rounded-xl border text-sm font-semibold transition ${
                                            method === "pin"
                                                ? "bg-brand-navy text-white border-brand-navy"
                                                : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                        }`}
                                        data-testid="cl-method-pin"
                                    >
                                        Security PIN
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {credLabel}
                                </label>
                                <div className="relative mt-2">
                                    {method === "pin" ? (
                                        <KeyRound className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    ) : (
                                        <Lock className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    )}
                                    <Input
                                        type={credType}
                                        inputMode={method === "pin" ? "numeric" : "text"}
                                        maxLength={method === "pin" ? 6 : 128}
                                        pattern={method === "pin" ? "[0-9]*" : undefined}
                                        value={credential}
                                        onChange={(e) => setCredential(e.target.value)}
                                        placeholder={credPlaceholder}
                                        className="pl-9 h-11 rounded-xl"
                                        required
                                        data-testid="cl-credential"
                                    />
                                </div>
                            </div>

                            {err && (
                                <div className="text-sm text-rose-600 bg-rose-50 rounded-xl px-3 py-2"
                                    data-testid="cl-error"
                                >
                                    {err}
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full rounded-full h-12 bg-brand-royal hover:bg-brand-hover text-white text-base"
                                data-testid="cl-submit"
                            >
                                {loading ? "Signing in…" : "Sign In"} <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>

                            <div className="text-center">
                                <Link
                                    to="/customer-recover"
                                    className="text-xs font-semibold text-brand-royal hover:underline"
                                    data-testid="cl-forgot-link"
                                >
                                    Forgot Password / PIN?
                                </Link>
                            </div>

                            <div className="flex items-start gap-2 text-xs text-slate-500 italic">
                                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>
                                    Customer credentials are set by our branch team. Please contact us
                                    if you don&apos;t have your Customer ID yet.
                                </span>
                            </div>
                        </form>
                    ) : (
                        <form className="mt-6 space-y-4" onSubmit={saveNew} data-testid="cl-change-form">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    New {credLabel}
                                </label>
                                <Input
                                    type="password"
                                    inputMode={method === "pin" ? "numeric" : "text"}
                                    maxLength={method === "pin" ? 6 : 128}
                                    pattern={method === "pin" ? "[0-9]*" : undefined}
                                    value={newCred}
                                    onChange={(e) => setNewCred(e.target.value)}
                                    placeholder={method === "pin" ? "New 4–6 digit PIN" : "New password (min 6)"}
                                    className="mt-2 h-11 rounded-xl"
                                    required
                                    data-testid="cl-new-cred"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Confirm New {credLabel}
                                </label>
                                <Input
                                    type="password"
                                    inputMode={method === "pin" ? "numeric" : "text"}
                                    maxLength={method === "pin" ? 6 : 128}
                                    pattern={method === "pin" ? "[0-9]*" : undefined}
                                    value={newCred2}
                                    onChange={(e) => setNewCred2(e.target.value)}
                                    placeholder="Re-enter to confirm"
                                    className="mt-2 h-11 rounded-xl"
                                    required
                                    data-testid="cl-new-cred-confirm"
                                />
                            </div>
                            {err && (
                                <div className="text-sm text-rose-600 bg-rose-50 rounded-xl px-3 py-2"
                                    data-testid="cl-change-error"
                                >
                                    {err}
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={saving}
                                className="w-full rounded-full h-12 bg-brand-navy hover:bg-brand-royal text-white text-base"
                                data-testid="cl-save-change"
                            >
                                {saving ? "Saving…" : `Save & Continue`} <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </form>
                    )}
                </Card>
            </main>

            <footer className="py-6 text-center text-xs text-slate-500">
                © {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.
            </footer>
        </div>
    );
}
